import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import { hasFullAccess } from '../lib/platform';
import UpgradeScreen from '../components/UpgradeScreen';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Search,
  Info,
  LogOut,
  Menu,
  X,
  MapPin,
  Calendar,
  Loader2,
  ChevronLeft,
  Droplets,
  Wind,
  Mountain,
  Thermometer,
  Shield,
  CheckCircle,
  XCircle,
  Bot,
  Sparkles,
  FileText,
  Sun,
  Snowflake,
  CloudRain,
  TrendingUp,
  Flame,
  Waves,
  Home,
  Target,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Building2
} from 'lucide-react';

const DiagnosticClimatiquePage = () => {
  const { user, logout } = useAuth();

  const isClubMember = hasFullAccess(user?.subscription_tier);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [communes, setCommunes] = useState([]);
  const [selectedCommune, setSelectedCommune] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [climate, setClimate] = useState(null);
  const [diagnostic, setDiagnostic] = useState(null);
  const [loading, setLoading] = useState(false);
  const [climateLoading, setClimateLoading] = useState(false);

  // Search communes
  useEffect(() => {
    const searchCommunes = async () => {
      if (searchQuery.length < 2) {
        setCommunes([]);
        return;
      }
      
      setSearchLoading(true);
      try {
        const response = await axios.get(`${API}/georisques/search`, {
          params: { q: searchQuery }
        });
        setCommunes(response.data || []);
      } catch (error) {
        console.error('Error searching communes:', error);
      } finally {
        setSearchLoading(false);
      }
    };
    
    const debounce = setTimeout(searchCommunes, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  if (!isClubMember) {
    return (
      <UpgradeScreen
        icon={AlertTriangle}
        iconBgClass="from-amber-500 to-orange-600"
        iconColorClass="text-amber-500"
        buttonGradientClass="from-amber-500 to-orange-600"
        title="Diagnostic Climatique"
        currentTier={user?.subscription_tier}
        features={[
          { icon: Thermometer, title: "Risques climatiques", description: "Analysez les risques climatiques de vos investissements" },
          { icon: Droplets, title: "Risques d'inondation", description: "Identifiez les zones inondables et les niveaux de risque" },
          { icon: Shield, title: "Recommandations", description: "Recevez des conseils pour anticiper l'impact climatique" }
        ]}
      />
    );
  }

  // Fetch analyse
  const fetchAnalyse = async (commune) => {
    if (!commune) return;
    
    setLoading(true);
    setClimateLoading(true);
    try {
      // Fetch climate stats and full diagnostic in parallel (sans georisques)
      const [climateResponse, diagnosticResponse] = await Promise.all([
        axios.get(`${API}/climate/stats/${commune.code}`).catch(() => null),
        axios.get(`${API}/climate/diagnostic/${commune.code}`).catch(() => null)
      ]);
      
      if (climateResponse) {
        setClimate(climateResponse.data);
      }
      if (diagnosticResponse) {
        setDiagnostic(diagnosticResponse.data);
      }
    } catch (error) {
      console.error('Error fetching analyse:', error);
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setLoading(false);
      setClimateLoading(false);
    }
  };

  const handleSelectCommune = (commune) => {
    setSelectedCommune(commune);
    setSearchQuery(commune.nom);
    setCommunes([]);
    fetchAnalyse(commune);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Dashboard
                </Button>
              </Link>
              <Link to="/dashboard" className="hidden md:flex">
                <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" />
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/profile" className="hidden md:flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#0D47A1] flex items-center justify-center text-white text-sm font-medium">
                    {user?.name?.charAt(0).toUpperCase()}
                  </div>
                )}
              </Link>
              <Button variant="ghost" size="sm" onClick={logout} className="hidden md:flex text-slate-600 hover:text-red-600">
                <LogOut className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 py-4 px-4 space-y-2">
            <Link to="/dashboard" className="block px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100">Tableau de bord</Link>
            <Link to="/profile" className="block px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100">Mon Profil</Link>
            <button onClick={logout} className="w-full text-left px-4 py-2 rounded-lg text-red-600 hover:bg-red-50">Déconnexion</button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
              <Sun className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-3">
                Diagnostic Climatique
                <Badge className="bg-white/20 text-white flex items-center gap-1">
                  <Bot className="w-3 h-3" /> IA
                </Badge>
              </h1>
              <p className="text-cyan-100 mt-1">Climat actuel, projections 2050 et impact investissement par commune</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Section */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Input */}
              <div className="flex-1 relative">
                <Label htmlFor="commune-search" className="text-sm font-medium mb-2 block">
                  Rechercher une commune
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="commune-search"
                    placeholder="Ex: Lyon, Paris, Bordeaux, Nîmes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="commune-search"
                  />
                  {searchLoading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />
                  )}
                </div>
                
                {/* Communes Dropdown */}
                {communes.length > 0 && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {communes.map((commune) => (
                      <button
                        key={commune.code}
                        className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center justify-between border-b border-slate-100 last:border-0"
                        onClick={() => handleSelectCommune(commune)}
                        data-testid={`commune-${commune.code}`}
                      >
                        <div>
                          <div className="font-medium text-slate-900">{commune.nom}</div>
                          <div className="text-sm text-slate-500">
                            {commune.code_postal} - {commune.departement}
                          </div>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {commune.population?.toLocaleString()} hab.
                        </Badge>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Search Button */}
              <div className="flex items-end">
                <Button
                  onClick={() => selectedCommune && fetchAnalyse(selectedCommune)}
                  disabled={!selectedCommune || loading}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="analyse-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                  Analyser le climat
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading && (
          <Card className="mb-6">
            <CardContent className="p-12 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-amber-500 mx-auto mb-4" />
              <p className="text-slate-600">Analyse en cours via Géorisques...</p>
            </CardContent>
          </Card>
        )}

        {(climate || diagnostic) && !loading && (
          <>
            {/* Summary Card */}
            <Card className="mb-6 border-2 border-cyan-300 bg-gradient-to-r from-cyan-50 to-blue-50">
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <Sun className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">{diagnostic?.commune || climate?.commune}</h2>
                      <p className="text-slate-500">
                        {diagnostic?.department} • Zone {diagnostic?.climate_zone?.name || climate?.climate_zone}
                        {diagnostic?.is_coastal && <Badge className="ml-2 bg-blue-100 text-blue-700">Littoral</Badge>}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {diagnostic?.population && (
                      <p className="text-sm text-slate-500">
                        Population: {diagnostic.population.toLocaleString()} habitants
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Climate Section - ERA5 Data */}
            {climate && (
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                    <Sun className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Données Climatiques</h2>
                    <p className="text-sm text-slate-500">Zone {climate.climate_zone} • {climate.period}</p>
                  </div>
                </div>

                {/* Temperature & Precipitation Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Temperature Card */}
                  <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                          <Thermometer className="w-5 h-5 text-cyan-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">Températures</p>
                          <p className="text-xs text-slate-500">Moyennes et records</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 bg-white/60 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Flame className="w-4 h-4 text-red-500" />
                            <span className="text-sm text-slate-600">Record chaleur</span>
                          </div>
                          <span className="font-bold text-red-600">{climate.temperature.record_max}°C</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-white/60 rounded-lg">
                          <div className="flex items-center gap-2">
                            <Snowflake className="w-4 h-4 text-blue-500" />
                            <span className="text-sm text-slate-600">Record froid</span>
                          </div>
                          <span className="font-bold text-blue-600">{climate.temperature.record_min}°C</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-white/60 rounded-lg">
                          <span className="text-sm text-slate-600">Moyenne annuelle</span>
                          <span className="font-bold text-slate-900">{climate.temperature.mean_annual}°C</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-cyan-200">
                          <div className="text-center p-2 bg-amber-100 rounded-lg">
                            <Sun className="w-4 h-4 text-amber-600 mx-auto mb-1" />
                            <p className="text-xs text-slate-500">Été</p>
                            <p className="font-bold text-amber-700">{climate.temperature.mean_summer}°C</p>
                          </div>
                          <div className="text-center p-2 bg-blue-100 rounded-lg">
                            <Snowflake className="w-4 h-4 text-blue-600 mx-auto mb-1" />
                            <p className="text-xs text-slate-500">Hiver</p>
                            <p className="font-bold text-blue-700">{climate.temperature.mean_winter}°C</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-center p-2 bg-red-50 rounded-lg">
                            <p className="text-lg font-bold text-red-600">{climate.temperature.hot_days_per_year}</p>
                            <p className="text-xs text-slate-500">Jours &gt;30°C/an</p>
                          </div>
                          <div className="text-center p-2 bg-blue-50 rounded-lg">
                            <p className="text-lg font-bold text-blue-600">{climate.temperature.frost_days_per_year}</p>
                            <p className="text-xs text-slate-500">Jours de gel/an</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Precipitation Card */}
                  <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <CloudRain className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">Précipitations</p>
                          <p className="text-xs text-slate-500">Cumuls et fréquence</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 bg-white/60 rounded-lg">
                          <span className="text-sm text-slate-600">Cumul annuel</span>
                          <span className="font-bold text-indigo-600">{climate.precipitation.annual_mm} mm</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-white/60 rounded-lg">
                          <span className="text-sm text-slate-600">Record journalier</span>
                          <span className="font-bold text-purple-600">{climate.precipitation.max_daily_mm} mm</span>
                        </div>
                        <div className="flex items-center justify-between p-2 bg-white/60 rounded-lg">
                          <span className="text-sm text-slate-600">Jours de pluie/an</span>
                          <span className="font-bold text-slate-900">{climate.precipitation.rainy_days}</span>
                        </div>
                        {climate.precipitation.dry_months?.length > 0 && (
                          <div className="p-2 bg-amber-50 rounded-lg">
                            <p className="text-xs text-slate-500 mb-1">Mois secs</p>
                            <p className="text-sm font-medium text-amber-700">
                              {climate.precipitation.dry_months.join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Climate Trend */}
                {climate.trend && (
                  <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <TrendingUp className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-slate-900 mb-2">Tendance climatique (30 ans)</h3>
                          <p className="text-sm text-slate-600 mb-3">{climate.trend.description}</p>
                          <div className="flex flex-wrap gap-4">
                            <div className="flex items-center gap-2 px-3 py-2 bg-white/60 rounded-lg">
                              <Thermometer className="w-4 h-4 text-red-500" />
                              <span className="text-sm text-slate-600">Température:</span>
                              <span className={`font-bold ${climate.trend.temp_change_30y > 0 ? 'text-red-600' : 'text-blue-600'}`}>
                                {climate.trend.temp_change_30y > 0 ? '+' : ''}{climate.trend.temp_change_30y}°C
                              </span>
                            </div>
                            <div className="flex items-center gap-2 px-3 py-2 bg-white/60 rounded-lg">
                              <CloudRain className="w-4 h-4 text-blue-500" />
                              <span className="text-sm text-slate-600">Précipitations:</span>
                              <span className={`font-bold ${climate.trend.precip_change > 0 ? 'text-blue-600' : 'text-amber-600'}`}>
                                {climate.trend.precip_change > 0 ? '+' : ''}{climate.trend.precip_change}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* ERA5 Source */}
                <div className="mt-4 text-center text-sm text-slate-500">
                  <p>
                    Données climatiques: {climate.source} •{' '}
                    <a 
                      href="https://cds.climate.copernicus.eu" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-cyan-600 hover:underline"
                    >
                      Copernicus Climate Data Store
                    </a>
                  </p>
                </div>
              </div>
            )}

            {climateLoading && !climate && (
              <Card className="mb-6">
                <CardContent className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-500 mx-auto mb-3" />
                  <p className="text-slate-600">Chargement des données climatiques ERA5...</p>
                </CardContent>
              </Card>
            )}

            {/* Projections 2050 Section */}
            {diagnostic?.projections_2050 && (
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Projections 2050</h2>
                    <p className="text-sm text-slate-500">Scénarios GIEC (CMIP6 / DRIAS)</p>
                  </div>
                </div>

                {/* Scenarios Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* Optimistic */}
                  <Card className="border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-green-800 text-sm">Optimiste</p>
                          <p className="text-xs text-green-600">SSP1-2.6</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Température</span>
                          <span className="font-bold text-red-500 flex items-center">
                            <ArrowUpRight className="w-3 h-3 mr-1" />
                            +{diagnostic.projections_2050.scenarios.optimistic.temp_increase}°C
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Précipitations</span>
                          <span className={`font-bold flex items-center ${diagnostic.projections_2050.scenarios.optimistic.precip_change > 0 ? 'text-blue-500' : 'text-amber-500'}`}>
                            {diagnostic.projections_2050.scenarios.optimistic.precip_change > 0 ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
                            {diagnostic.projections_2050.scenarios.optimistic.precip_change}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Jours canicule</span>
                          <span className="font-bold text-orange-500">{diagnostic.projections_2050.scenarios.optimistic.heatwave_days} j/an</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Moderate */}
                  <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 ring-2 ring-amber-300">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Clock className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-amber-800 text-sm">Modéré</p>
                          <p className="text-xs text-amber-600">SSP2-4.5 (le plus probable)</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Température</span>
                          <span className="font-bold text-red-600 flex items-center text-lg">
                            <ArrowUpRight className="w-4 h-4 mr-1" />
                            +{diagnostic.projections_2050.scenarios.moderate.temp_increase}°C
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Précipitations</span>
                          <span className={`font-bold flex items-center ${diagnostic.projections_2050.scenarios.moderate.precip_change > 0 ? 'text-blue-500' : 'text-amber-600'}`}>
                            {diagnostic.projections_2050.scenarios.moderate.precip_change > 0 ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
                            {diagnostic.projections_2050.scenarios.moderate.precip_change}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Jours canicule</span>
                          <span className="font-bold text-orange-600 text-lg">{diagnostic.projections_2050.scenarios.moderate.heatwave_days} j/an</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Pessimistic */}
                  <Card className="border-red-200 bg-gradient-to-br from-red-50 to-rose-50">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
                          <AlertTriangle className="w-4 h-4 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-red-800 text-sm">Pessimiste</p>
                          <p className="text-xs text-red-600">SSP5-8.5</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Température</span>
                          <span className="font-bold text-red-700 flex items-center">
                            <ArrowUpRight className="w-3 h-3 mr-1" />
                            +{diagnostic.projections_2050.scenarios.pessimistic.temp_increase}°C
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Précipitations</span>
                          <span className={`font-bold flex items-center ${diagnostic.projections_2050.scenarios.pessimistic.precip_change > 0 ? 'text-blue-500' : 'text-red-500'}`}>
                            {diagnostic.projections_2050.scenarios.pessimistic.precip_change > 0 ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
                            {diagnostic.projections_2050.scenarios.pessimistic.precip_change}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-600">Jours canicule</span>
                          <span className="font-bold text-red-600">{diagnostic.projections_2050.scenarios.pessimistic.heatwave_days} j/an</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Current vs 2050 Comparison */}
                {diagnostic.projections_2050.current_vs_2050 && (
                  <Card className="mb-4 border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                    <CardContent className="p-5">
                      <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-purple-600" />
                        Évolution attendue (scénario modéré)
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-white/60 rounded-xl">
                          <Sun className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                          <p className="text-xs text-slate-500 mb-1">Jours de canicule</p>
                          <div className="flex items-center justify-center gap-2">
                            <span className="text-lg font-bold text-slate-600">{diagnostic.projections_2050.current_vs_2050.heatwave_days?.current}</span>
                            <ArrowUpRight className="w-4 h-4 text-red-500" />
                            <span className="text-lg font-bold text-red-600">{diagnostic.projections_2050.current_vs_2050.heatwave_days?.projected_moderate}</span>
                          </div>
                          <p className="text-xs text-slate-400">par an</p>
                        </div>
                        
                        {diagnostic.projections_2050.current_vs_2050.tropical_nights && (
                          <div className="text-center p-3 bg-white/60 rounded-xl">
                            <Thermometer className="w-6 h-6 text-red-500 mx-auto mb-2" />
                            <p className="text-xs text-slate-500 mb-1">Nuits tropicales</p>
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-lg font-bold text-slate-600">{diagnostic.projections_2050.current_vs_2050.tropical_nights.current}</span>
                              <ArrowUpRight className="w-4 h-4 text-red-500" />
                              <span className="text-lg font-bold text-red-600">{diagnostic.projections_2050.current_vs_2050.tropical_nights['2050']}</span>
                            </div>
                            <p className="text-xs text-slate-400">&gt;20°C la nuit</p>
                          </div>
                        )}
                        
                        <div className="text-center p-3 bg-white/60 rounded-xl">
                          <Snowflake className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                          <p className="text-xs text-slate-500 mb-1">Jours de gel</p>
                          <span className="text-lg font-bold text-blue-600">{diagnostic.projections_2050.current_vs_2050.frost_days_change} j</span>
                          <p className="text-xs text-slate-400">évolution</p>
                        </div>
                        
                        <div className="text-center p-3 bg-white/60 rounded-xl">
                          <Droplets className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                          <p className="text-xs text-slate-500 mb-1">Risque sécheresse</p>
                          <p className="text-sm font-bold text-slate-600">{diagnostic.projections_2050.current_vs_2050.drought_risk?.current}</p>
                          <ArrowUpRight className="w-3 h-3 text-red-500 mx-auto" />
                          <p className="text-sm font-bold text-amber-600">{diagnostic.projections_2050.current_vs_2050.drought_risk?.['2050']}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Sea Level Rise (if coastal) */}
                {diagnostic.projections_2050.sea_level_rise && (
                  <Card className="mb-4 border-blue-300 bg-gradient-to-r from-blue-50 to-cyan-50">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Waves className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-slate-900 mb-2">Montée du niveau de la mer</h3>
                          <p className="text-sm text-slate-600 mb-3">{diagnostic.projections_2050.sea_level_rise.description}</p>
                          <div className="flex flex-wrap gap-3">
                            <div className="px-3 py-2 bg-green-100 rounded-lg">
                              <p className="text-xs text-green-600">Optimiste</p>
                              <p className="font-bold text-green-700">+{diagnostic.projections_2050.sea_level_rise.optimistic_cm} cm</p>
                            </div>
                            <div className="px-3 py-2 bg-amber-100 rounded-lg">
                              <p className="text-xs text-amber-600">Modéré</p>
                              <p className="font-bold text-amber-700">+{diagnostic.projections_2050.sea_level_rise.moderate_cm} cm</p>
                            </div>
                            <div className="px-3 py-2 bg-red-100 rounded-lg">
                              <p className="text-xs text-red-600">Pessimiste</p>
                              <p className="font-bold text-red-700">+{diagnostic.projections_2050.sea_level_rise.pessimistic_cm} cm</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Mountain Specific (if mountain zone) */}
                {diagnostic.projections_2050.mountain_specific && (
                  <Card className="mb-4 border-slate-300 bg-gradient-to-r from-slate-50 to-gray-100">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <Mountain className="w-6 h-6 text-slate-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-slate-900 mb-2">Impact montagne</h3>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="p-2 bg-white rounded-lg text-center">
                              <p className="text-xs text-slate-500">Enneigement</p>
                              <p className="font-bold text-blue-600">{diagnostic.projections_2050.mountain_specific.snow_cover_reduction_percent}%</p>
                            </div>
                            <div className="p-2 bg-white rounded-lg text-center">
                              <p className="text-xs text-slate-500">Glaciers</p>
                              <p className="font-bold text-cyan-600">{diagnostic.projections_2050.mountain_specific.glacier_retreat_percent}%</p>
                            </div>
                            <div className="p-2 bg-white rounded-lg text-center">
                              <p className="text-xs text-slate-500">Saison ski</p>
                              <p className="font-bold text-slate-600">{diagnostic.projections_2050.mountain_specific.ski_season_reduction_days} jours</p>
                            </div>
                            <div className="p-2 bg-white rounded-lg text-center">
                              <p className="text-xs text-slate-500">Permafrost</p>
                              <p className="font-bold text-amber-600">{diagnostic.projections_2050.mountain_specific.permafrost_degradation}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Description */}
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl">
                  <p className="text-sm text-purple-800">
                    <strong>Analyse :</strong> {diagnostic.projections_2050.description}
                  </p>
                </div>
              </div>
            )}

            {/* Investment Impact Section */}
            {diagnostic?.investment_impact && (
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Impact Investissement Immobilier</h2>
                    <p className="text-sm text-slate-500">Analyse pour les investisseurs</p>
                  </div>
                </div>

                <Card className={`border-2 ${
                  diagnostic.investment_impact.risk_level === 'élevé' ? 'border-red-300 bg-red-50/50' :
                  diagnostic.investment_impact.risk_level === 'modéré' ? 'border-amber-300 bg-amber-50/50' :
                  'border-green-300 bg-green-50/50'
                }`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-slate-900">Niveau de risque climatique</h3>
                      <Badge className={`text-sm px-3 py-1 ${
                        diagnostic.investment_impact.risk_level === 'élevé' ? 'bg-red-100 text-red-700' :
                        diagnostic.investment_impact.risk_level === 'modéré' ? 'bg-amber-100 text-amber-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {diagnostic.investment_impact.risk_level?.toUpperCase()}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* Risks */}
                      <div className="p-4 bg-white/60 rounded-xl">
                        <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" /> Risques principaux
                        </h4>
                        <ul className="space-y-1">
                          {diagnostic.investment_impact.main_risks?.map((risk, i) => (
                            <li key={i} className="text-sm text-slate-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
                              {risk}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Opportunities */}
                      <div className="p-4 bg-white/60 rounded-xl">
                        <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Opportunités
                        </h4>
                        <ul className="space-y-1">
                          {diagnostic.investment_impact.opportunities?.map((opp, i) => (
                            <li key={i} className="text-sm text-slate-600 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                              {opp}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {/* Recommendations */}
                    <div className="p-4 bg-blue-50 rounded-xl mb-4">
                      <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                        <Info className="w-4 h-4" /> Recommandations
                      </h4>
                      <ul className="space-y-1">
                        {diagnostic.investment_impact.recommendations?.map((rec, i) => (
                          <li key={i} className="text-sm text-slate-600 flex items-start gap-2">
                            <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5"></span>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Value Impact */}
                    <div className="p-3 bg-slate-100 rounded-lg">
                      <p className="text-sm text-slate-700">
                        <strong>Impact sur la valeur :</strong> {diagnostic.investment_impact.value_impact}
                      </p>
                    </div>

                    {/* Coastal Risks */}
                    {diagnostic.investment_impact.coastal_risks && (
                      <div className="mt-4 p-4 bg-blue-100 rounded-xl">
                        <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                          <Waves className="w-4 h-4" /> Risques littoraux spécifiques
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                          <div className="text-center p-2 bg-white/60 rounded-lg">
                            <p className="text-xs text-slate-500">Montée des eaux</p>
                            <p className="font-bold text-blue-700">{diagnostic.investment_impact.coastal_risks.sea_level_rise}</p>
                          </div>
                          <div className="text-center p-2 bg-white/60 rounded-lg">
                            <p className="text-xs text-slate-500">Érosion</p>
                            <p className="font-bold text-amber-700">{diagnostic.investment_impact.coastal_risks.erosion}</p>
                          </div>
                          <div className="text-center p-2 bg-white/60 rounded-lg">
                            <p className="text-xs text-slate-500">Assurance</p>
                            <p className="font-bold text-red-700">{diagnostic.investment_impact.coastal_risks.insurance}</p>
                          </div>
                        </div>
                        <ul className="space-y-1">
                          {diagnostic.investment_impact.coastal_risks.recommendations?.map((rec, i) => (
                            <li key={i} className="text-sm text-blue-800 flex items-start gap-2">
                              <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5"></span>
                              {rec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {/* Empty State */}
        {!climate && !diagnostic && !loading && (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-6">
                <Sun className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3">Diagnostic Climatique</h2>
              <p className="text-slate-600 max-w-xl mx-auto mb-6">
                Recherchez une commune pour connaître son climat actuel, les projections 2050 
                et l'impact sur l'investissement immobilier.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto mb-6">
                <div className="p-4 bg-cyan-50 rounded-xl border border-cyan-100">
                  <Thermometer className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-cyan-700">Climat actuel</p>
                  <p className="text-xs text-cyan-500">Températures & précipitations</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                  <Target className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-purple-700">Projections 2050</p>
                  <p className="text-xs text-purple-500">3 scénarios GIEC</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                  <Building2 className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                  <p className="text-sm font-medium text-emerald-700">Impact Immobilier</p>
                  <p className="text-xs text-emerald-500">Risques & opportunités</p>
                </div>
              </div>

              <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 max-w-md mx-auto">
                <Sparkles className="w-5 h-5 text-cyan-600 mx-auto mb-2" />
                <p className="text-sm text-cyan-700">
                  Tapez le nom d'une ville dans le champ de recherche ci-dessus pour commencer l'analyse
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="text-center text-sm text-slate-500">
          <p>
            Données:{' '}
            <a href="https://cds.climate.copernicus.eu" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
              ERA5 Copernicus
            </a>
            {' '}•{' '}
            <a href="https://drias-climat.fr" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
              DRIAS Météo-France
            </a>
            {' '}• Projections CMIP6/GIEC
          </p>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticClimatiquePage;
